/**
 * OPERARIOS Y POLIVALENCIAS
 *
 * PaginaOperarios — lista global con botón de alta inline.
 * PaginaOperario  — ficha con 3 tabs: Datos, Centros, Polivalencias.
 *
 * Tipo FIJO: badge info. Tipo VOLANTE: badge aviso.
 * El código y el tipo son inmutables: se muestran deshabilitados en la
 * edición. Que el campo no se pueda tocar ya lo dice; no hace falta un
 * rótulo "(INMUTABLE)" además.
 *
 * POLIVALENCIAS: MATRIZ APPEND-ONLY (FASE 3.2B-iii-b)
 *
 * Una evaluación no se edita ni se borra nunca — para corregirla se anula
 * (con motivo obligatorio) y se registra una nueva. `evaluado_por` y
 * `anulada_por` los deriva el backend de la sesión: el formulario ni
 * siquiera tiene un campo para eso. "Nivel actual", vigente/caducada y el
 * histórico salen tal cual del backend (que a su vez los saca de
 * `fn_operario_nivel_en_fecha` y `v_operario_polivalencia_historico`); esta
 * pantalla no recalcula nada de eso, sólo lo pinta.
 *
 * SELECCIÓN DE CENTRO: BUSCAR, NO DESPLEGAR
 *
 * Con cuatro centros una lista o un `<select>` con todos vale. Con
 * decenas o cientos no: por eso el alta y la pestaña Centros comparten
 * `SelectorCentros`, un buscador por nombre/código de centro o de
 * cliente que nunca pinta más de un puñado de resultados a la vez.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Outlet, useLocation, useOutletContext, useParams } from 'react-router-dom';

import {
  api,
  type AptitudTipo,
  type CentroOperario,
  type Centro,
  type Cliente,
  type EstadoPolivalenciaOperario,
  type EvaluacionPolivalencia,
  FalloApi,
  type FilaMatrizPolivalencia,
  type EspecialidadOperativa,
  type OperarioDetalle,
  type OperarioResumen,
  type RequisitoAptitud,
} from '../api.ts';
import {
  alternarOrden,
  Avatar,
  CabeceraOrdenable,
  Cargando,
  CampoTexto,
  Error as ErrorCaja,
  fecha,
  fechaHora,
  FiltrosResponsive,
  Panel,
  useDatos,
  Vacio,
} from '../componentes/base.tsx';
import { DisponibilidadOperario } from '../componentes/disponibilidad-operario.tsx';
import { ExcepcionesDisponibilidad } from '../componentes/excepciones-disponibilidad.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

/**
 * F12.1 — un técnico con partes activos en un centro no puede dejarlo (TECNICO_CON_PARTES_ACTIVOS): además
 * del mensaje, los partes que lo impiden, enlazados, para reasignarlos o resolverlos. Nada se desasigna solo.
 */
export function ErrorCambioCentro({ error }: { error: unknown }): ReactNode {
  const partes = error instanceof FalloApi && error.codigo === 'TECNICO_CON_PARTES_ACTIVOS'
    ? ((error.detalles as { partes?: { id: string; codigo: string; titulo: string; centro: string }[] } | undefined)?.partes ?? [])
    : [];
  return (
    <>
      <ErrorCaja error={error} />
      {partes.length > 0 && (
        <ul className="lista-sin-estilo" aria-label="Partes activos del técnico" style={{ marginTop: 8 }}>
          {partes.map((p) => (
            <li key={p.id}><Link to={`/mantenimiento/partes/${p.id}`}>{p.titulo}</Link> <span className="suave pequena">{p.centro} · {p.codigo}</span></li>
          ))}
        </ul>
      )}
    </>
  );
}

// -------------------------------------------------------------------
//  Todos los centros de todos los clientes al alcance del usuario.
//
//  No existe un `/api/centros` global (ver operarios.ts backend), así que
//  se compone aquí: la lista de clientes y, para cada uno, sus centros.
//  `habilitado` evita el N+1 cuando quien mira la pantalla no puede
//  gestionar centros (sólo hace falta para el buscador de alta/gestión).
//
//  Estado explícito de cuatro fases, no un booleano `cargando` + `datos`:
//    inactivo  → no se ha pedido nada todavía (habilitado=false)
//    cargando  → petición en curso
//    listo     → datos reales, incluido el `[]` legítimo
//    error     → la petición falló
//  Que "listo" con `centros: []` sea un estado distinto de "inactivo" o
//  "cargando" es la clave: así el buscador sólo puede decir "no quedan
//  centros disponibles" cuando de verdad terminó de cargar y no hay
//  ninguno — nunca mientras la respuesta real todavía está en camino.
// -------------------------------------------------------------------

type EstadoCentros =
  | { fase: 'inactivo' }
  | { fase: 'cargando' }
  | { fase: 'listo'; centros: Centro[] }
  | { fase: 'error'; error: unknown };

async function pedirTodosCentros(): Promise<Centro[]> {
  const rc = await api.get<{ clientes: { id: string }[] }>('/api/clientes');
  const listas = await Promise.all(
    rc.clientes.map((cl) => api.get<{ centros: Centro[] }>(`/api/clientes/${cl.id}/centros`)),
  );
  return listas.flatMap((l) => l.centros);
}

function useTodosCentros(habilitado: boolean): EstadoCentros {
  const [estado, setEstado] = useState<EstadoCentros>({ fase: 'inactivo' });

  useEffect(() => {
    if (!habilitado) {
      setEstado({ fase: 'inactivo' });
      return;
    }
    let vivo = true;
    setEstado({ fase: 'cargando' });
    pedirTodosCentros()
      .then((centros) => {
        if (vivo) setEstado({ fase: 'listo', centros });
      })
      .catch((error: unknown) => {
        if (vivo) setEstado({ fase: 'error', error });
      });
    return () => {
      vivo = false;
    };
  }, [habilitado]);

  return estado;
}

/** Evita repetir el switch de fase en cada sitio que usa el buscador de centros. */
function PanelCentros({
  estado,
  children,
}: {
  estado: EstadoCentros;
  children: (centros: Centro[]) => ReactNode;
}): ReactNode {
  if (estado.fase === 'inactivo' || estado.fase === 'cargando') return <Cargando />;
  if (estado.fase === 'error') return <ErrorCaja error={estado.error} />;
  return <>{children(estado.centros)}</>;
}

// -------------------------------------------------------------------
//  PaginaOperarios — lista
// -------------------------------------------------------------------

type FiltroTipoOperario = 'TODOS' | 'FIJO' | 'VOLANTE' | 'GLOBAL';
type FiltroEstadoOperario = 'TODOS' | 'ACTIVOS' | 'INACTIVOS';
type OrdenOperarios = 'apellidos' | 'codigo' | 'tipo' | 'activo';
type Dir = 'asc' | 'desc';

/**
 * `apellidos ASC` porque es exactamente el `ORDER BY o.apellidos, o.nombre,
 * o.codigo` que el backend ya aplicaba antes de esta fase — migrar a
 * orden explícito no cambia lo que se ve por defecto.
 */
const DEFAULTS_OPERARIOS = {
  q: '', tipo: 'TODOS', estado: 'TODOS', cliente: '', centro: '', orden: 'apellidos', dir: 'asc',
} as const;

export function PaginaOperarios(): ReactNode {
  const { puede } = useSesion();
  const lista = useListaUrl(DEFAULTS_OPERARIOS);

  const q = lista.valor('q');
  const tipo = lista.valor('tipo') as FiltroTipoOperario;
  const filtroEstado = lista.valor('estado') as FiltroEstadoOperario;
  const idCliente = lista.valor('cliente');
  const idCentro = lista.valor('centro');
  const orden = lista.valor('orden') as OrdenOperarios;
  const dir = lista.valor('dir') as Dir;

  function ordenarPor(columna: OrdenOperarios): void {
    const siguiente = alternarOrden(columna, orden, dir);
    lista.fijarVarios({ orden: siguiente.orden, dir: siguiente.dir });
  }

  function elegirCliente(valor: string): void {
    //  Cambiar de cliente invalida el centro elegido (era de otro cliente,
    //  o de ninguno): se fija en la MISMA actualización para no dejar un
    //  instante con cliente y centro incoherentes entre sí.
    lista.fijarVarios({ cliente: valor, centro: DEFAULTS_OPERARIOS.centro });
  }

  //  FASE 3.3-D3: el badge «Filtros (N)» cuenta sólo refinamiento — nunca
  //  `q` (es búsqueda, no filtro) ni `orden`/`dir` (es orden, no filtro).
  const contadorFiltros = [
    tipo !== DEFAULTS_OPERARIOS.tipo,
    filtroEstado !== DEFAULTS_OPERARIOS.estado,
    idCliente !== DEFAULTS_OPERARIOS.cliente,
    idCentro !== DEFAULTS_OPERARIOS.centro,
  ].filter(Boolean).length;

  // El propio backend hace de "recorte AND filtros": nada de esto puede
  // ampliar lo que ya decide `alcanceDeCentros()` en el servidor.
  const consulta = new URLSearchParams();
  if (q.trim() !== '') consulta.set('busqueda', q.trim());
  if (tipo !== 'TODOS') consulta.set('tipo', tipo);
  if (filtroEstado !== 'TODOS') consulta.set('activo', filtroEstado === 'ACTIVOS' ? 'true' : 'false');
  if (idCliente !== '') consulta.set('idCliente', idCliente);
  if (idCentro !== '') consulta.set('idCentro', idCentro);
  if (orden !== DEFAULTS_OPERARIOS.orden) consulta.set('orderBy', orden);
  if (dir !== DEFAULTS_OPERARIOS.dir) consulta.set('orderDir', dir);

  const operarios = useDatos(
    () => api.get<{ operarios: OperarioResumen[] }>(`/api/operarios?${consulta.toString()}`),
    [consulta.toString()],
  );

  const clientesDisponibles = useDatos(() => api.get<{ clientes: Cliente[] }>('/api/clientes'), []);
  const centrosDelCliente = useDatos(
    () =>
      idCliente === ''
        ? Promise.resolve({ centros: [] as Centro[] })
        : api.get<{ centros: Centro[] }>(`/api/clientes/${idCliente}/centros`),
    [idCliente],
  );

  const [mostrarNuevo, setMostrarNuevo] = useState(false);
  const centrosAlta = useTodosCentros(mostrarNuevo);

  return (
    <Shell
      titulo="Operarios"
      acciones={
        puede('operarios:crear') && !mostrarNuevo ? (
          <button className="principal" onClick={() => setMostrarNuevo(true)}>
            Nuevo operario
          </button>
        ) : mostrarNuevo ? (
          <button onClick={() => setMostrarNuevo(false)}>Cancelar</button>
        ) : undefined
      }
    >
      {mostrarNuevo && (
        <div className="panel">
          <div className="panel-cabecera">
            <h2>Nuevo operario</h2>
          </div>
          <PanelCentros estado={centrosAlta}>
            {(listaCentros) => (
              <FormNuevoOperario
                centros={listaCentros}
                alCrear={() => {
                  setMostrarNuevo(false);
                  operarios.recargar();
                }}
                alCancelar={() => setMostrarNuevo(false)}
              />
            )}
          </PanelCentros>
        </div>
      )}

      <div className="panel">
        <FiltrosResponsive
          contador={contadorFiltros}
          busqueda={
            <div className="campo campo-busqueda">
              <label htmlFor="buscar-operario">Buscar</label>
              <input
                id="buscar-operario"
                type="text"
                placeholder="Código, nombre, apellidos, documento, centro o cliente…"
                value={q}
                onChange={(e) => lista.fijar('q', e.target.value)}
              />
            </div>
          }
        >
          <div className="campo">
            <label htmlFor="filtro-tipo-operario">Tipo</label>
            <select
              id="filtro-tipo-operario"
              value={tipo}
              onChange={(e) => lista.fijar('tipo', e.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="FIJO">Fijo</option>
              <option value="VOLANTE">Volante</option>
              <option value="GLOBAL">Global (mantenimiento)</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-estado-operario">Estado</label>
            <select
              id="filtro-estado-operario"
              value={filtroEstado}
              onChange={(e) => lista.fijar('estado', e.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="ACTIVOS">Activos</option>
              <option value="INACTIVOS">Inactivos</option>
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-cliente-operario">Cliente</label>
            <select
              id="filtro-cliente-operario"
              value={idCliente}
              onChange={(e) => elegirCliente(e.target.value)}
            >
              <option value="">Todos</option>
              {(clientesDisponibles.datos?.clientes ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.razonSocial}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="filtro-centro-operario">Centro</label>
            <select
              id="filtro-centro-operario"
              value={idCentro}
              disabled={idCliente === ''}
              onChange={(e) => lista.fijar('centro', e.target.value)}
            >
              <option value="">{idCliente === '' ? 'Elige un cliente primero' : 'Todos'}</option>
              {(centrosDelCliente.datos?.centros ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.descripcion}
                </option>
              ))}
            </select>
          </div>
          {!lista.esPorDefecto && (
            <button
              type="button"
              className="pequeno"
              style={{ alignSelf: 'flex-end' }}
              onClick={lista.limpiar}
            >
              Limpiar filtros
            </button>
          )}
        </FiltrosResponsive>

        <Panel estado={operarios}>
          {(d) =>
            d.operarios.length === 0 ? (
              <Vacio
                titulo={lista.esPorDefecto ? 'No hay operarios a tu alcance' : 'No hay operarios que coincidan con los filtros'}
              >
                {lista.esPorDefecto && puede('operarios:crear') && (
                  <p className="suave">Crea el primero con el botón «Nuevo operario».</p>
                )}
              </Vacio>
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <CabeceraOrdenable
                        texto="Código"
                        columna="codigo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('codigo')}
                      />
                      <CabeceraOrdenable
                        texto="Nombre"
                        columna="apellidos"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('apellidos')}
                      />
                      <CabeceraOrdenable
                        texto="Tipo"
                        columna="tipo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('tipo')}
                      />
                      <th className="num">Centros</th>
                      <CabeceraOrdenable
                        texto="Estado"
                        columna="activo"
                        ordenActual={orden}
                        dirActual={dir}
                        onClick={() => ordenarPor('activo')}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {d.operarios.map((o) => (
                      <tr key={o.id}>
                        <td className="cod">
                          <Link to={`/operarios/${o.id}`}>{o.codigo}</Link>
                        </td>
                        <td>{o.apellidos}, {o.nombre}</td>
                        <td>
                          <BadgeTipo tipo={o.tipo} />
                        </td>
                        <td className="num">{o.nCentros}</td>
                        <td>
                          <span className={`etiqueta ${o.activo ? 'exito' : 'neutra'}`}>
                            {o.activo ? 'ACTIVO' : 'INACTIVO'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Panel>
      </div>
    </Shell>
  );
}

// -------------------------------------------------------------------
//  PaginaOperario — ficha con tabs
// -------------------------------------------------------------------

// -------------------------------------------------------------------
//  DisposicionOperario — FASE 3.3-C5
//
//  Mismo patrón que `DisposicionCentro` (centro.tsx): la cabecera y los
//  datos comunes (`operario`, `centrosHabilitados`) se cargan UNA vez
//  aquí y viajan por el contexto del outlet — cambiar de tab no repite
//  ningún fetch, exactamente como antes de C5 (esas dos llamadas ya eran
//  incondicionales al nivel de toda la ficha).
//
//  `/operarios/:id` (índice) y `/operarios/:id/datos` renderizan el
//  MISMO componente `TabDatosOperario` — es un alias, no un redirect: la
//  URL no se reescribe sola. El resto de tabs siempre navegan a su ruta
//  explícita, así que interactuar con las tabs deja la URL en su forma
//  canónica de forma natural, sin forzar nada.
// -------------------------------------------------------------------

type EstadoDatos<T> = ReturnType<typeof useDatos<T>>;

export interface ContextoOperario {
  readonly operario: EstadoDatos<OperarioDetalle>;
  readonly centrosHabilitados: EstadoDatos<{ centros: CentroOperario[] }>;
}

export function useOperario(): ContextoOperario {
  return useOutletContext<ContextoOperario>();
}

const SECCIONES_OPERARIO = [
  { a: 'datos', texto: 'Datos' },
  { a: 'centros', texto: 'Centros' },
  { a: 'polivalencias', texto: 'Polivalencias' },
  { a: 'aptitud', texto: 'Aptitud' },
  { a: 'disponibilidad', texto: 'Disponibilidad' },
] as const;

export function DisposicionOperario(): ReactNode {
  const { id = '' } = useParams();
  const location = useLocation();
  const operario = useDatos(
    () => api.get<OperarioDetalle>(`/api/operarios/${id}`),
    [id],
  );
  const centrosHabilitados = useDatos(
    () => api.get<{ centros: CentroOperario[] }>(`/api/operarios/${id}/centros`),
    [id],
  );

  if (operario.cargando && operario.datos === null) {
    return (
      <Shell titulo="Operario">
        <Cargando />
      </Shell>
    );
  }
  if (operario.error !== null || operario.datos === null) {
    return (
      <Shell titulo="Operario">
        <ErrorCaja error={operario.error} reintentar={operario.recargar} />
      </Shell>
    );
  }

  const d = operario.datos;
  const nombreCompleto = `${d.nombre} ${d.apellidos}`;
  const base = `/operarios/${id}`;

  //  "Datos" es el único con alias (la ruta índice, sin sufijo): se
  //  marca activo tanto en `base` como en `base/datos`. El resto sólo
  //  tiene una URL real cada uno.
  function activa(seccion: (typeof SECCIONES_OPERARIO)[number]['a']): boolean {
    if (seccion === 'datos') return location.pathname === base || location.pathname === `${base}/datos`;
    return location.pathname === `${base}/${seccion}`;
  }

  return (
    <Shell
      migas={[
        { texto: 'Operarios', a: '/operarios' },
        { texto: nombreCompleto },
      ]}
      titulo={
        <span className="fila">
          <span>{nombreCompleto}</span>
          <span
            className={`etiqueta ${d.activo ? 'exito' : 'neutra'}`}
            title={d.activo ? 'Este operario está activo en el maestro' : 'Este operario está dado de baja en el maestro'}
          >
            {d.activo ? 'ACTIVO' : 'INACTIVO'}
          </span>
        </span>
      }
      subtitulo={
        <>
          <span className="mono">{d.codigo}</span>
          <span aria-hidden="true" className="tenue">·</span>
          <BadgeTipo tipo={d.tipo} />
        </>
      }
      tabs={
        <nav className="tabs" aria-label="Secciones del operario">
          {SECCIONES_OPERARIO.map((s) => (
            //  `Link`, no `NavLink`: `NavLink` sólo aplica el `aria-current`
            //  que se le pasa cuando SU PROPIO cálculo de `isActive` (comparar
            //  `to` con la ruta actual) coincide — y "Datos" tiene dos rutas
            //  válidas (la base y `/datos`), así que ese cálculo interno no
            //  sirve aquí. Se calcula `activa()` una vez y se aplica entero.
            <Link
              key={s.a}
              to={`${base}/${s.a}`}
              className={activa(s.a) ? 'activo' : ''}
              aria-current={activa(s.a) ? 'page' : undefined}
            >
              {s.texto}
            </Link>
          ))}
        </nav>
      }
    >
      <Outlet context={{ operario, centrosHabilitados } satisfies ContextoOperario} />
    </Shell>
  );
}

export function TabDatosOperario(): ReactNode {
  const { operario, centrosHabilitados } = useOperario();
  const { puede } = useSesion();
  return (
    <div className="panel">
      <Panel estado={operario}>
        {(d) => (
          puede('operarios:editar') ? (
            <FormEditarOperario
              datos={d}
              alEditar={operario.recargar}
            />
          ) : (
            <VistaOperario datos={d} />
          )
        )}
      </Panel>
      {puede('operarios:habilitar_centro') && (
        <Panel estado={operario}>
          {(d) => (
            <Panel estado={centrosHabilitados}>
              {(c) => (
                <FormCambiarTipo
                  operario={d}
                  centrosActuales={c.centros}
                  alCambiar={() => {
                    operario.recargar();
                    centrosHabilitados.recargar();
                  }}
                />
              )}
            </Panel>
          )}
        </Panel>
      )}
    </div>
  );
}

export function TabCentrosOperario(): ReactNode {
  const { operario, centrosHabilitados } = useOperario();
  const { puede } = useSesion();
  return (
    <div className="panel">
      <Panel estado={operario}>
        {(d) => (
          <Panel estado={centrosHabilitados}>
            {(c) => (
              <TabCentros
                operario={d}
                centros={c.centros}
                alCambiar={centrosHabilitados.recargar}
                puedGestionar={puede('operarios:habilitar_centro')}
              />
            )}
          </Panel>
        )}
      </Panel>
    </div>
  );
}

export function TabPolivalenciasOperario(): ReactNode {
  const { operario } = useOperario();
  const { puede } = useSesion();
  return (
    <div className="panel">
      <Panel estado={operario}>
        {(d) => (
          <TabPolivalencias
            operario={d}
            puedeEvaluar={puede('polivalencias:evaluar')}
            puedeAnular={puede('polivalencias:anular')}
          />
        )}
      </Panel>
    </div>
  );
}

export function TabAptitudOperario(): ReactNode {
  const { operario } = useOperario();
  return (
    <div className="panel">
      <Panel estado={operario}>
        {(d) => <TabAptitud operario={d} />}
      </Panel>
    </div>
  );
}

export function TabDisponibilidadOperario(): ReactNode {
  const { operario } = useOperario();
  const { puede } = useSesion();
  return (
    <Panel estado={operario}>
      {(d) => (
        <>
          <DisponibilidadOperario idOperario={d.id} puedeEditar={puede('disponibilidad:gestionar')} />
          <ExcepcionesDisponibilidad idOperario={d.id} puedeEditar={puede('disponibilidad:gestionar')} />
        </>
      )}
    </Panel>
  );
}

// -------------------------------------------------------------------
//  Vista de solo lectura de datos
// -------------------------------------------------------------------

function VistaOperario({ datos: d }: { datos: OperarioDetalle }): ReactNode {
  return (
    <div className="panel-cuerpo">
      <div className="datos">
        <div className="dato">
          <div className="etiqueta-dato">Código</div>
          <div className="valor-dato mono">{d.codigo}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Tipo</div>
          <div className="valor-dato"><BadgeTipo tipo={d.tipo} /></div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Especialidad operativa</div>
          <div className="valor-dato">{ETIQUETA_ESPECIALIDAD[d.especialidadOperativa ?? 'LIMPIEZA']}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Nombre</div>
          <div className="valor-dato">{d.nombre}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Apellidos</div>
          <div className="valor-dato">{d.apellidos}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Documento de identidad</div>
          <div className="valor-dato">{d.documentoIdentidad ?? '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Teléfono</div>
          <div className="valor-dato">{d.telefono ?? '—'}</div>
        </div>
        <div className="dato">
          <div className="etiqueta-dato">Fecha de alta</div>
          <div className="valor-dato">{fecha(d.fechaAlta)}</div>
        </div>
        {d.fechaBaja !== null && (
          <div className="dato">
            <div className="etiqueta-dato">Fecha de baja</div>
            <div className="valor-dato">{fecha(d.fechaBaja)}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
//  Formulario de edición (campos mutables)
// -------------------------------------------------------------------

function FormEditarOperario({
  datos,
  alEditar,
}: {
  datos: OperarioDetalle;
  alEditar: () => void;
}): ReactNode {
  const [nombre, setNombre] = useState(datos.nombre);
  const [apellidos, setApellidos] = useState(datos.apellidos);
  const [documentoIdentidad, setDocumentoIdentidad] = useState(datos.documentoIdentidad ?? '');
  const [telefono, setTelefono] = useState(datos.telefono ?? '');
  const [activo, setActivo] = useState(datos.activo);
  const [especialidad, setEspecialidad] = useState<EspecialidadOperativa>(datos.especialidadOperativa ?? 'LIMPIEZA');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});
  const [guardadoOk, setGuardadoOk] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    setGuardadoOk(false);
    try {
      await api.patch<OperarioDetalle>(`/api/operarios/${datos.id}`, {
        nombre,
        apellidos,
        documentoIdentidad: documentoIdentidad.trim() || null,
        telefono: telefono.trim() || null,
        activo,
        especialidadOperativa: especialidad,
      });
      setGuardadoOk(true);
      alEditar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor="operario-codigo">Código</label>
          <input id="operario-codigo" type="text" value={datos.codigo} disabled />
        </div>
        <div className="campo">
          <label htmlFor="operario-tipo">Tipo</label>
          <input id="operario-tipo" type="text" value={datos.tipo} disabled />
        </div>
        <SelectorEspecialidad id="operario-especialidad" valor={especialidad} cambiar={setEspecialidad} disabled={enviando} error={camposMal['especialidadOperativa']} />
        <CampoTexto
          label="Nombre"
          value={nombre}
          onChange={setNombre}
          error={camposMal['nombre']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Apellidos"
          value={apellidos}
          onChange={setApellidos}
          error={camposMal['apellidos']}
          disabled={enviando}
          required
        />
        <CampoTexto
          label="Documento de identidad"
          value={documentoIdentidad}
          onChange={setDocumentoIdentidad}
          error={camposMal['documentoIdentidad']}
          disabled={enviando}
        />
        <CampoTexto
          label="Teléfono"
          value={telefono}
          onChange={setTelefono}
          error={camposMal['telefono']}
          disabled={enviando}
        />
      </div>
      <div className="campo" style={{ marginTop: 4 }}>
        <label
          style={{
            textTransform: 'none',
            fontSize: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            color: 'var(--texto)',
            fontWeight: 'normal',
            marginBottom: 0,
          }}
        >
          <input
            type="checkbox"
            style={{ width: 'auto' }}
            checked={activo}
            onChange={(ev) => { setActivo(ev.target.checked); setGuardadoOk(false); }}
            disabled={enviando}
          />
          Operario activo
        </label>
        <p className="tenue pequena" style={{ marginTop: 4, marginBottom: 0 }}>
          Desactivar no borra al operario ni sus centros o evaluaciones, y no toca las OT ya
          emitidas: sólo lo saca de las altas nuevas.
        </p>
      </div>
      {error !== null && <ErrorCaja error={error} />}
      {guardadoOk && (
        <div className="nota exito" role="status">
          Cambios guardados. El operario está{' '}
          <strong>{activo ? 'ACTIVO' : 'INACTIVO'}</strong>.
        </div>
      )}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Guardar cambios'}
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Cambio de tipo FIJO ↔ VOLANTE — operación dedicada y atómica
//
//  Nunca un <select> de tipo suelto: las reglas de cardinalidad son
//  distintas en cada dirección (FIJO exige 1 centro principal; VOLANTE
//  exige 2 o más), así que cada dirección tiene su propio formulario y
//  llama a `POST /api/operarios/:id/cambiar-tipo`, nunca al PATCH
//  genérico. El backend hace el cambio de tipo y la recomposición de
//  centros en una sola transacción.
// -------------------------------------------------------------------

function FormCambiarTipo({
  operario,
  centrosActuales,
  alCambiar,
}: {
  operario: OperarioDetalle;
  centrosActuales: CentroOperario[];
  alCambiar: () => void;
}): ReactNode {
  const [mostrando, setMostrando] = useState(false);
  const [aGlobal, setAGlobal] = useState<{ enviando: boolean; error: unknown }>({ enviando: false, error: null });

  //  F12.5 — técnico GLOBAL ↔ restringido. A GLOBAL: sus partes siguen a su alcance (no se bloquea). De GLOBAL a
  //  FIJO: el servidor bloquea si deja partes activos fuera de ese centro (TECNICO_CON_PARTES_ACTIVOS, con la lista).
  async function convertirEnGlobal() {
    setAGlobal({ enviando: true, error: null });
    try {
      await api.post<OperarioDetalle>(`/api/operarios/${operario.id}/cambiar-tipo`, { tipo: 'GLOBAL' });
      setAGlobal({ enviando: false, error: null });
      alCambiar();
    } catch (err) {
      setAGlobal({ enviando: false, error: err });
    }
  }

  if (!mostrando) {
    return (
      <div className="panel-cuerpo">
        <div className="fila" style={{ gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setMostrando(true)}>
            {operario.tipo === 'GLOBAL' ? 'Restringir a un centro (FIJO)' : `Cambiar a ${operario.tipo === 'FIJO' ? 'VOLANTE' : 'FIJO'}`}
          </button>
          {operario.tipo !== 'GLOBAL' && operario.especialidadOperativa === 'MANTENIMIENTO' && (
            <button onClick={() => { void convertirEnGlobal(); }} disabled={aGlobal.enviando}>
              {aGlobal.enviando ? 'Cambiando…' : 'Convertir en técnico global'}
            </button>
          )}
        </div>
        {aGlobal.error !== null && <ErrorCambioCentro error={aGlobal.error} />}
      </div>
    );
  }

  return operario.tipo === 'FIJO' ? (
    <FormAVolante
      operario={operario}
      centroActual={centrosActuales[0] ?? null}
      alCambiar={() => { setMostrando(false); alCambiar(); }}
      alCancelar={() => setMostrando(false)}
    />
  ) : (
    <FormAFijo
      operario={operario}
      centrosActuales={centrosActuales}
      desdeGlobal={operario.tipo === 'GLOBAL'}
      alCambiar={() => { setMostrando(false); alCambiar(); }}
      alCancelar={() => setMostrando(false)}
    />
  );
}

function FormAVolante({
  operario,
  centroActual,
  alCambiar,
  alCancelar,
}: {
  operario: OperarioDetalle;
  centroActual: CentroOperario | null;
  alCambiar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [seleccionados, setSeleccionados] = useState<Centro[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});
  const todosCentros = useTodosCentros(true);

  const excluir = new Set([
    ...(centroActual !== null ? [centroActual.idCentro] : []),
    ...seleccionados.map((c) => c.id),
  ]);

  async function confirmar() {
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<OperarioDetalle>(`/api/operarios/${operario.id}/cambiar-tipo`, {
        tipo: 'VOLANTE',
        centrosAdicionales: seleccionados.map((c) => c.id),
      });
      alCambiar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel-cuerpo">
      <h3 style={{ marginTop: 0 }}>Cambiar a VOLANTE</h3>
      <p className="tenue pequena">
        Un VOLANTE necesita al menos dos centros. El centro actual se conserva como está;
        añade uno o más para completar el cambio.
      </p>

      <div className="campo">
        <label>Centro actual</label>
        {centroActual !== null && (
          <div>
            <span className="chip-centro">
              <Avatar nombre={centroActual.clienteRazonSocial} pequeno />
              <span>{centroActual.codigo} · Principal</span>
            </span>
          </div>
        )}
      </div>

      <div className="campo" style={{ marginTop: 8 }}>
        <label>Añadir al menos otro centro</label>
        {camposMal['centrosAdicionales'] !== undefined && (
          <span className="campo-error">{camposMal['centrosAdicionales']}</span>
        )}
        {seleccionados.length > 0 && (
          <div style={{ marginTop: 6, marginBottom: 4 }}>
            {seleccionados.map((c) => (
              <span className="chip-centro" key={c.id}>
                <Avatar nombre={c.clienteRazonSocial} pequeno />
                <span>{c.codigo}</span>
                <button
                  type="button"
                  aria-label={`Quitar ${c.codigo} de la selección`}
                  onClick={() => setSeleccionados((prev) => prev.filter((x) => x.id !== c.id))}
                  disabled={enviando}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <PanelCentros estado={todosCentros}>
          {(lista) => (
            <SelectorCentros
              centros={lista}
              excluir={excluir}
              onSeleccionar={(c) => setSeleccionados((prev) => [...prev, c])}
              deshabilitado={enviando}
            />
          )}
        </PanelCentros>
      </div>

      {error !== null && <ErrorCambioCentro error={error} />}
      <div className="fila-botones">
        <button
          className="principal"
          disabled={enviando || seleccionados.length === 0}
          onClick={() => { void confirmar(); }}
        >
          {enviando ? 'Cambiando…' : 'Confirmar cambio'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

function FormAFijo({
  operario,
  centrosActuales,
  desdeGlobal = false,
  alCambiar,
  alCancelar,
}: {
  operario: OperarioDetalle;
  centrosActuales: CentroOperario[];
  /** F12.5: un GLOBAL no tiene centros; el destino es cualquiera. */
  desdeGlobal?: boolean;
  alCambiar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [idCentroDestino, setIdCentroDestino] = useState('');
  const todos = useTodosCentros(desdeGlobal);
  const opciones = desdeGlobal
    ? (todos.fase === 'listo' ? todos.centros.map((c) => ({ idCentro: c.id, codigo: c.codigo, clienteRazonSocial: c.clienteRazonSocial })) : [])
    : centrosActuales;
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function confirmar() {
    if (idCentroDestino === '') return;
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<OperarioDetalle>(`/api/operarios/${operario.id}/cambiar-tipo`, {
        tipo: 'FIJO',
        idCentroDestino,
      });
      alCambiar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="panel-cuerpo">
      <h3 style={{ marginTop: 0 }}>Cambiar a FIJO</h3>
      <p className="tenue pequena">
        Elige en qué centro quedará fijo. Ese centro pasará a ser el principal; el resto se
        retira.
      </p>

      <div className="campo">
        <label htmlFor="centro-fijo-destino">Centro fijo</label>
        <select
          id="centro-fijo-destino"
          value={idCentroDestino}
          onChange={(ev) => setIdCentroDestino(ev.target.value)}
          disabled={enviando}
        >
          <option value="">Elige un centro…</option>
          {opciones.map((c) => (
            <option key={c.idCentro} value={c.idCentro}>
              {c.codigo} — {c.clienteRazonSocial}
            </option>
          ))}
        </select>
        {camposMal['idCentroDestino'] !== undefined && (
          <span className="campo-error">{camposMal['idCentroDestino']}</span>
        )}
      </div>

      {error !== null && <ErrorCambioCentro error={error} />}
      <div className="fila-botones">
        <button className="principal" disabled={enviando || idCentroDestino === ''} onClick={() => { void confirmar(); }}>
          {enviando ? 'Cambiando…' : 'Confirmar cambio'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
//  Tab Centros
// -------------------------------------------------------------------

function TabCentros({
  operario,
  centros,
  alCambiar,
  puedGestionar,
}: {
  operario: OperarioDetalle;
  centros: CentroOperario[];
  alCambiar: () => void;
  puedGestionar: boolean;
}): ReactNode {
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const [mostrarSelector, setMostrarSelector] = useState(false);

  const todosCentros = useTodosCentros(puedGestionar);
  const idsAsignados = new Set(centros.map((c) => c.idCentro));

  async function anadir(c: Centro) {
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/operarios/${operario.id}/centros`, {
        idCentro: c.id,
        esPrincipal: centros.length === 0,
      });
      //  Se deja el buscador abierto: un VOLANTE se da de alta en varios
      //  centros de una sentada, sin tener que reabrir el panel cada vez.
      alCambiar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  async function cambiar(c: Centro) {
    setEnviando(true);
    setError(null);
    try {
      await api.post(`/api/operarios/${operario.id}/centros/cambiar`, { idCentroNuevo: c.id });
      setMostrarSelector(false);
      alCambiar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  async function quitar(idCentro: string) {
    setEnviando(true);
    setError(null);
    try {
      await api.del(`/api/operarios/${operario.id}/centros/${idCentro}`);
      alCambiar();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  const enElPiso = operario.tipo === 'VOLANTE' && centros.length <= 2;

  return (
    <div className="panel-cuerpo">
      {centros.length === 0 ? (
        operario.tipo === 'GLOBAL'
          ? <Vacio titulo="Técnico global: sin centros asignados">Se le puede asignar trabajo de mantenimiento en cualquier centro.</Vacio>
          : <Vacio titulo="Sin centros habilitados" />
      ) : (
        <div className="tabla-desliza">
          <table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Centro</th>
                <th>Principal</th>
                {puedGestionar && operario.tipo === 'VOLANTE' && <th />}
              </tr>
            </thead>
            <tbody>
              {centros.map((c) => (
                <tr key={c.idCentro}>
                  <td>
                    <Link to={`/clientes/${c.idCliente}`} className="con-avatar">
                      <Avatar nombre={c.clienteRazonSocial} pequeno />
                      <span className="texto-secundario">{c.clienteRazonSocial}</span>
                    </Link>
                  </td>
                  <td>
                    <Link to={`/centros/${c.idCentro}`}>
                      <span className="texto-principal">{c.descripcion}</span>{' '}
                      <span className="cod suave">{c.codigo}</span>
                    </Link>
                  </td>
                  <td>
                    {c.esPrincipal ? (
                      <span className="etiqueta exito">Principal</span>
                    ) : (
                      <span className="etiqueta neutra">—</span>
                    )}
                  </td>
                  {puedGestionar && operario.tipo === 'VOLANTE' && (
                    <td className="derecha">
                      <button
                        className="pequeno"
                        disabled={enviando || enElPiso}
                        title={enElPiso ? 'Un VOLANTE necesita al menos 2 centros: añade otro antes de quitar éste.' : undefined}
                        onClick={() => { void quitar(c.idCentro); }}
                      >
                        Quitar
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error !== null && <ErrorCambioCentro error={error} />}

      {puedGestionar && operario.tipo === 'FIJO' && (
        <div style={{ marginTop: 16 }}>
          {!mostrarSelector ? (
            <button onClick={() => setMostrarSelector(true)} disabled={enviando}>
              Cambiar centro
            </button>
          ) : (
            <>
              <div className="fila separa" style={{ marginBottom: 4 }}>
                <h3 style={{ fontSize: 14 }}>Cambiar centro</h3>
                <button className="pequeno" onClick={() => setMostrarSelector(false)} disabled={enviando}>
                  Cancelar
                </button>
              </div>
              <PanelCentros estado={todosCentros}>
                {(lista) => (
                  <SelectorCentros
                    centros={lista}
                    excluir={idsAsignados}
                    onSeleccionar={(c) => { void cambiar(c); }}
                    deshabilitado={enviando}
                  />
                )}
              </PanelCentros>
            </>
          )}
        </div>
      )}

      {puedGestionar && operario.tipo === 'VOLANTE' && (
        <div style={{ marginTop: 16 }}>
          {!mostrarSelector ? (
            <button onClick={() => setMostrarSelector(true)} disabled={enviando}>
              Añadir centro
            </button>
          ) : (
            <>
              <div className="fila separa" style={{ marginBottom: 4 }}>
                <h3 style={{ fontSize: 14 }}>Añadir centro</h3>
                <button className="pequeno" onClick={() => setMostrarSelector(false)} disabled={enviando}>
                  Cerrar
                </button>
              </div>
              <PanelCentros estado={todosCentros}>
                {(lista) => (
                  <SelectorCentros
                    centros={lista}
                    excluir={idsAsignados}
                    onSeleccionar={(c) => { void anadir(c); }}
                    deshabilitado={enviando}
                  />
                )}
              </PanelCentros>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
//  Buscador de centros — usado en el alta y en la gestión de centros
// -------------------------------------------------------------------

const LIMITE_RESULTADOS_CENTRO = 30;

function SelectorCentros({
  centros,
  excluir,
  onSeleccionar,
  deshabilitado,
}: {
  centros: Centro[];
  excluir: Set<string>;
  onSeleccionar: (c: Centro) => void;
  deshabilitado?: boolean;
}): ReactNode {
  const [busqueda, setBusqueda] = useState('');

  const disponibles = centros.filter((c) => !excluir.has(c.id));
  const t = busqueda.trim().toLowerCase();
  const filtrados =
    t === ''
      ? disponibles
      : disponibles.filter(
          (c) =>
            c.codigo.toLowerCase().includes(t) ||
            c.descripcion.toLowerCase().includes(t) ||
            c.clienteCodigo.toLowerCase().includes(t) ||
            c.clienteRazonSocial.toLowerCase().includes(t),
        );
  const mostrados = filtrados.slice(0, LIMITE_RESULTADOS_CENTRO);

  return (
    <div className="selector-centros">
      <div className="campo">
        <label htmlFor="buscar-centro">Buscar centro</label>
        <input
          id="buscar-centro"
          type="text"
          placeholder="Nombre o código de centro o de cliente…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          disabled={deshabilitado}
          autoFocus
        />
      </div>
      {disponibles.length === 0 ? (
        <p className="suave pequena">No quedan centros disponibles para añadir.</p>
      ) : mostrados.length === 0 ? (
        <p className="suave pequena">Sin resultados para «{busqueda}».</p>
      ) : (
        <>
          <ul className="lista-resultados">
            {mostrados.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  className="resultado-centro"
                  onClick={() => onSeleccionar(c)}
                  disabled={deshabilitado}
                >
                  <Avatar nombre={c.clienteRazonSocial} pequeno />
                  <span className="resultado-texto">
                    <strong>
                      {c.descripcion} <span className="mono suave">{c.codigo}</span>
                    </strong>
                    <span className="suave">{c.clienteRazonSocial} · {c.clienteCodigo}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {filtrados.length > LIMITE_RESULTADOS_CENTRO && (
            <p className="tenue pequena" style={{ marginTop: 6, marginBottom: 0 }}>
              Mostrando {LIMITE_RESULTADOS_CENTRO} de {filtrados.length} resultados. Afina la
              búsqueda para encontrar el centro.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
//  Formulario de creación
// -------------------------------------------------------------------

function FormNuevoOperario({
  centros,
  alCrear,
  alCancelar,
}: {
  centros: Centro[];
  alCrear: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [tipo, setTipo] = useState<'FIJO' | 'VOLANTE' | 'GLOBAL'>('FIJO');
  const [especialidad, setEspecialidad] = useState<EspecialidadOperativa>('LIMPIEZA');
  const [documentoIdentidad, setDocumentoIdentidad] = useState('');
  const [telefono, setTelefono] = useState('');
  const [seleccionados, setSeleccionados] = useState<Centro[]>([]);
  const [idPrincipal, setIdPrincipal] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  function cambiarTipo(nuevo: 'FIJO' | 'VOLANTE' | 'GLOBAL') {
    setTipo(nuevo);
    setSeleccionados([]);
    setIdPrincipal('');
  }

  function agregarCentro(c: Centro) {
    if (tipo === 'FIJO') {
      setSeleccionados([c]);
      setIdPrincipal(c.id);
    } else {
      setSeleccionados((prev) => [...prev, c]);
      setIdPrincipal((prev) => (prev === '' ? c.id : prev));
    }
  }

  function quitarCentro(id: string) {
    setSeleccionados((prev) => prev.filter((c) => c.id !== id));
    setIdPrincipal((prev) => (prev === id ? '' : prev));
  }

  //  El array que se manda nunca contiene una fila «placeholder»: si no
  //  hay ningún centro elegido, se manda `centros: []` (length 0), no una
  //  fila con idCentro=''. Es justo lo que hace que el backend distinga
  //  «sin centro» de «con un centro válido» y devuelva la validación 400
  //  en vez de que un uuid vacío reviente en la base como un 500.
  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      const centrosPayload = seleccionados.map((c) => ({
        idCentro: c.id,
        esPrincipal: tipo === 'FIJO' ? true : c.id === idPrincipal,
      }));

      await api.post<OperarioDetalle>('/api/operarios', {
        codigo,
        nombre,
        apellidos,
        tipo,
        especialidadOperativa: especialidad,
        documentoIdentidad: documentoIdentidad.trim() || null,
        telefono: telefono.trim() || null,
        centros: centrosPayload,
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  const idsSeleccionados = new Set(seleccionados.map((c) => c.id));
  //  F12.5: un técnico GLOBAL no tiene centros (trabaja en cualquiera).
  const puedeAnadirMas = tipo !== 'GLOBAL' && (tipo === 'VOLANTE' || seleccionados.length === 0);

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <CampoTexto label="Código" value={codigo} onChange={setCodigo} error={camposMal['codigo']} disabled={enviando} required />
        <CampoTexto label="Nombre" value={nombre} onChange={setNombre} error={camposMal['nombre']} disabled={enviando} required />
        <CampoTexto label="Apellidos" value={apellidos} onChange={setApellidos} error={camposMal['apellidos']} disabled={enviando} required />
        <div className="campo">
          <label htmlFor="nuevo-operario-tipo">Tipo</label>
          <select
            id="nuevo-operario-tipo"
            value={tipo}
            onChange={(ev) => cambiarTipo(ev.target.value as 'FIJO' | 'VOLANTE' | 'GLOBAL')}
            disabled={enviando}
          >
            <option value="FIJO">FIJO</option>
            <option value="VOLANTE">VOLANTE</option>
            {/* F12.5: sólo un técnico de mantenimiento puede ser GLOBAL (sin centros, cualquier centro). */}
            <option value="GLOBAL" disabled={especialidad !== 'MANTENIMIENTO'}>GLOBAL (técnico de mantenimiento, cualquier centro)</option>
          </select>
          {camposMal['tipo'] !== undefined && <span className="campo-error">{camposMal['tipo']}</span>}
        </div>
        <SelectorEspecialidad id="nuevo-operario-especialidad" valor={especialidad}
          cambiar={(e) => { setEspecialidad(e); if (e !== 'MANTENIMIENTO' && tipo === 'GLOBAL') cambiarTipo('FIJO'); }}
          disabled={enviando} error={camposMal['especialidadOperativa']} />
        <CampoTexto label="Documento de identidad" value={documentoIdentidad} onChange={setDocumentoIdentidad} error={camposMal['documentoIdentidad']} disabled={enviando} />
        <CampoTexto label="Teléfono" value={telefono} onChange={setTelefono} error={camposMal['telefono']} disabled={enviando} />
      </div>

      <div className="campo" style={{ marginTop: 8 }}>
        <label>{tipo === 'FIJO' ? 'Centro (exactamente 1)' : tipo === 'GLOBAL' ? 'Centros' : 'Centros (mínimo 2)'}</label>
        {tipo === 'GLOBAL' && <p className="pequena tenue" style={{ margin: '4px 0' }}>Sin centros asignados: se le puede asignar trabajo en cualquier centro.</p>}
        {camposMal['centros'] !== undefined && <span className="campo-error">{camposMal['centros']}</span>}

        {seleccionados.length > 0 && (
          <div style={{ marginTop: 6, marginBottom: 4 }}>
            {seleccionados.map((c) => (
              <span className="chip-centro" key={c.id}>
                <Avatar nombre={c.clienteRazonSocial} pequeno />
                <span>
                  {c.codigo}
                  {tipo === 'VOLANTE' && c.id === idPrincipal ? ' · Principal' : ''}
                </span>
                {tipo === 'VOLANTE' && seleccionados.length > 1 && c.id !== idPrincipal && (
                  <button
                    type="button"
                    className="enlace"
                    style={{ fontSize: 11 }}
                    onClick={() => setIdPrincipal(c.id)}
                    disabled={enviando}
                  >
                    Hacer principal
                  </button>
                )}
                <button
                  type="button"
                  aria-label={`Quitar ${c.codigo} de la selección`}
                  onClick={() => quitarCentro(c.id)}
                  disabled={enviando}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {puedeAnadirMas && (
          <SelectorCentros
            centros={centros}
            excluir={idsSeleccionados}
            onSeleccionar={agregarCentro}
            deshabilitado={enviando}
          />
        )}
      </div>

      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear operario'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Tab Polivalencias — matriz, histórico, evaluar y anular
// -------------------------------------------------------------------

const ETIQUETA_ESTADO_POLIVALENCIA: Record<EstadoPolivalenciaOperario, string> = {
  SIN_EVALUAR: 'SIN EVALUAR',
  VIGENTE: 'VIGENTE',
  CADUCADA: 'CADUCADA',
};
const CLASE_ESTADO_POLIVALENCIA: Record<EstadoPolivalenciaOperario, string> = {
  SIN_EVALUAR: 'neutra',
  VIGENTE: 'exito',
  CADUCADA: 'aviso',
};

function BadgeEstadoPolivalencia({ estado }: { estado: EstadoPolivalenciaOperario }): ReactNode {
  return (
    <span className={`etiqueta ${CLASE_ESTADO_POLIVALENCIA[estado]}`}>
      {ETIQUETA_ESTADO_POLIVALENCIA[estado]}
    </span>
  );
}

type FiltroEstadoPolivalencia = 'TODAS' | EstadoPolivalenciaOperario;

function TabPolivalencias({
  operario,
  puedeEvaluar,
  puedeAnular,
}: {
  operario: OperarioDetalle;
  puedeEvaluar: boolean;
  puedeAnular: boolean;
}): ReactNode {
  const matriz = useDatos(
    () => api.get<{ matriz: FilaMatrizPolivalencia[] }>(`/api/operarios/${operario.id}/polivalencias`),
    [operario.id],
  );
  const [seleccionada, setSeleccionada] = useState<FilaMatrizPolivalencia | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<FiltroEstadoPolivalencia>('TODAS');

  if (seleccionada !== null) {
    return (
      <PanelHistoricoPolivalencia
        operario={operario}
        polivalencia={seleccionada}
        puedeEvaluar={puedeEvaluar}
        puedeAnular={puedeAnular}
        alVolver={() => setSeleccionada(null)}
        alCambiar={matriz.recargar}
      />
    );
  }

  return (
    <div className="panel-cuerpo sin-relleno">
      <Panel estado={matriz}>
        {(d) => {
          const t = busqueda.trim().toLowerCase();
          const filtradas = d.matriz.filter((f) => {
            if (filtroEstado !== 'TODAS' && f.estado !== filtroEstado) return false;
            if (t === '') return true;
            return f.codigo.toLowerCase().includes(t) || f.descripcion.toLowerCase().includes(t);
          });

          return (
            <>
              <div className="filtros">
                <div className="campo campo-busqueda">
                  <label htmlFor="buscar-polivalencia">Buscar</label>
                  <input
                    id="buscar-polivalencia"
                    type="text"
                    placeholder="Código o descripción…"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>
                <div className="campo">
                  <label htmlFor="filtro-estado-polivalencia">Estado</label>
                  <select
                    id="filtro-estado-polivalencia"
                    value={filtroEstado}
                    onChange={(e) => setFiltroEstado(e.target.value as FiltroEstadoPolivalencia)}
                  >
                    <option value="TODAS">Todas</option>
                    <option value="SIN_EVALUAR">Sin evaluar</option>
                    <option value="VIGENTE">Vigentes</option>
                    <option value="CADUCADA">Caducadas</option>
                  </select>
                </div>
              </div>

              {d.matriz.length === 0 ? (
                <Vacio titulo="No hay polivalencias en el catálogo" />
              ) : filtradas.length === 0 ? (
                <div className="panel-cuerpo">
                  <p className="suave">Sin resultados para los filtros actuales.</p>
                </div>
              ) : (
                <div className="tabla-desliza">
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Polivalencia</th>
                        <th>Nivel actual</th>
                        <th>Fecha inicio</th>
                        <th>Última revisión</th>
                        <th>Caducidad</th>
                        <th>Estado</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filtradas.map((f) => (
                        <tr key={f.idPolivalencia}>
                          <td className="cod">{f.codigo}</td>
                          <td>
                            {f.descripcion}
                            {!f.activo && (
                              <span className="etiqueta neutra" style={{ marginLeft: 6 }}>
                                Inactiva
                              </span>
                            )}
                          </td>
                          <td className="num">{f.nivelActual === null ? '—' : `${f.nivelActual} %`}</td>
                          <td>{fecha(f.fechaInicio)}</td>
                          <td>{fecha(f.ultimaRevision)}</td>
                          <td>{fecha(f.caducidad)}</td>
                          <td>
                            <BadgeEstadoPolivalencia estado={f.estado} />
                          </td>
                          <td className="derecha">
                            <button className="pequeno" onClick={() => setSeleccionada(f)}>
                              Ver histórico
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        }}
      </Panel>
    </div>
  );
}

function PanelHistoricoPolivalencia({
  operario,
  polivalencia,
  puedeEvaluar,
  puedeAnular,
  alVolver,
  alCambiar,
}: {
  operario: OperarioDetalle;
  polivalencia: FilaMatrizPolivalencia;
  puedeEvaluar: boolean;
  puedeAnular: boolean;
  alVolver: () => void;
  alCambiar: () => void;
}): ReactNode {
  const historico = useDatos(
    () =>
      api.get<{ evaluaciones: EvaluacionPolivalencia[] }>(
        `/api/operarios/${operario.id}/polivalencias/${polivalencia.idPolivalencia}/evaluaciones`,
      ),
    [operario.id, polivalencia.idPolivalencia],
  );
  const [mostrarForm, setMostrarForm] = useState(false);

  return (
    <div className="panel-cuerpo">
      <div className="fila separa" style={{ alignItems: 'flex-start', marginBottom: 12 }}>
        <div>
          <button type="button" className="enlace" onClick={alVolver}>
            ← Volver a la matriz
          </button>
          <h3 style={{ marginTop: 6 }}>
            {polivalencia.codigo} · {polivalencia.descripcion}
          </h3>
        </div>
        {puedeEvaluar && polivalencia.activo && !mostrarForm && (
          <button className="principal" onClick={() => setMostrarForm(true)}>
            Nueva evaluación
          </button>
        )}
      </div>

      {puedeEvaluar && !polivalencia.activo && (
        <p className="tenue pequena" style={{ marginBottom: 12 }}>
          Esta polivalencia está inactiva en el catálogo: se conserva su histórico, pero no admite
          evaluaciones nuevas desde aquí.
        </p>
      )}

      {mostrarForm && (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-cabecera">
            <h2>Nueva evaluación</h2>
          </div>
          <FormNuevaEvaluacion
            operario={operario}
            polivalencia={polivalencia}
            alRegistrar={() => {
              setMostrarForm(false);
              historico.recargar();
              alCambiar();
            }}
            alCancelar={() => setMostrarForm(false)}
          />
        </div>
      )}

      <Panel estado={historico}>
        {(d) =>
          d.evaluaciones.length === 0 ? (
            <Vacio titulo="Sin evaluaciones registradas todavía" />
          ) : (
            <div className="tabla-desliza">
              <table>
                <thead>
                  <tr>
                    <th>Fecha evaluación</th>
                    <th>Nivel</th>
                    <th>Caducidad</th>
                    <th>Evaluado por</th>
                    <th>Observaciones</th>
                    <th>Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.evaluaciones.map((ev) => (
                    <FilaHistorico
                      key={ev.idEvaluacion}
                      operario={operario}
                      polivalencia={polivalencia}
                      evaluacion={ev}
                      puedeAnular={puedeAnular}
                      alAnular={() => {
                        historico.recargar();
                        alCambiar();
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Panel>
    </div>
  );
}

function FilaHistorico({
  operario,
  polivalencia,
  evaluacion: ev,
  puedeAnular,
  alAnular,
}: {
  operario: OperarioDetalle;
  polivalencia: FilaMatrizPolivalencia;
  evaluacion: EvaluacionPolivalencia;
  puedeAnular: boolean;
  alAnular: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function anular() {
    if (motivo.trim() === '') return;
    setEnviando(true);
    setError(null);
    try {
      await api.post(
        `/api/operarios/${operario.id}/polivalencias/${polivalencia.idPolivalencia}/evaluaciones/${ev.idEvaluacion}/anular`,
        { motivo: motivo.trim() },
      );
      setConfirmando(false);
      setMotivo('');
      alAnular();
    } catch (err) {
      setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <tr>
        <td>{fecha(ev.fechaEvaluacion)}</td>
        <td className="num">{ev.nivel} %</td>
        <td>{fecha(ev.fechaCaducidad)}</td>
        <td>{ev.evaluadorNombre}</td>
        <td className="suave">{ev.observaciones ?? '—'}</td>
        <td>
          <span className={`etiqueta ${ev.estado === 'ACTIVA' ? 'exito' : 'neutra'}`}>{ev.estado}</span>
          {ev.estado === 'ANULADA' && (
            <div className="pequena tenue" style={{ marginTop: 4 }}>
              {fechaHora(ev.anuladaEn)} · {ev.anuladorNombre ?? '—'}
              {ev.motivoAnulacion !== null && <> · «{ev.motivoAnulacion}»</>}
            </div>
          )}
        </td>
        <td className="derecha">
          {puedeAnular && ev.estado === 'ACTIVA' && !confirmando && (
            <button className="pequeno" onClick={() => setConfirmando(true)}>
              Anular
            </button>
          )}
        </td>
      </tr>
      {confirmando && (
        <tr>
          <td colSpan={7}>
            <div className="nota aviso">
              <p>
                ¿Anular la evaluación del {fecha(ev.fechaEvaluacion)} ({ev.nivel} %)? No se puede
                deshacer; si el nivel era incorrecto, registra una evaluación nueva después de
                anular ésta.
              </p>
              <CampoTexto
                label="Motivo de la anulación"
                value={motivo}
                onChange={setMotivo}
                disabled={enviando}
                required
              />
              {error !== null && <ErrorCaja error={error} />}
              <div className="fila-botones">
                <button
                  className="principal"
                  disabled={enviando || motivo.trim() === ''}
                  onClick={() => {
                    void anular();
                  }}
                >
                  {enviando ? 'Anulando…' : 'Confirmar anulación'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmando(false);
                    setMotivo('');
                    setError(null);
                  }}
                  disabled={enviando}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

const NIVELES_POLIVALENCIA = [0, 25, 50, 75, 100] as const;

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function FormNuevaEvaluacion({
  operario,
  polivalencia,
  alRegistrar,
  alCancelar,
}: {
  operario: OperarioDetalle;
  polivalencia: FilaMatrizPolivalencia;
  alRegistrar: () => void;
  alCancelar: () => void;
}): ReactNode {
  const [nivel, setNivel] = useState<number>(75);
  const [fechaEvaluacion, setFechaEvaluacion] = useState(hoyIso());
  const [observaciones, setObservaciones] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post<EvaluacionPolivalencia>(
        `/api/operarios/${operario.id}/polivalencias/${polivalencia.idPolivalencia}/evaluaciones`,
        {
          nivel,
          fecha: fechaEvaluacion,
          observaciones: observaciones.trim() || null,
        },
      );
      alRegistrar();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor="nueva-eval-nivel">Nivel</label>
          <select
            id="nueva-eval-nivel"
            value={nivel}
            onChange={(e) => setNivel(Number(e.target.value))}
            disabled={enviando}
          >
            {NIVELES_POLIVALENCIA.map((n) => (
              <option key={n} value={n}>
                {n} %
              </option>
            ))}
          </select>
          {camposMal['nivel'] !== undefined && <span className="campo-error">{camposMal['nivel']}</span>}
        </div>
        <div className="campo">
          <label htmlFor="nueva-eval-fecha">Fecha</label>
          <input
            id="nueva-eval-fecha"
            type="date"
            value={fechaEvaluacion}
            onChange={(e) => setFechaEvaluacion(e.target.value)}
            disabled={enviando}
          />
          {camposMal['fecha'] !== undefined && <span className="campo-error">{camposMal['fecha']}</span>}
        </div>
        <CampoTexto
          label="Observaciones"
          value={observaciones}
          onChange={setObservaciones}
          disabled={enviando}
        />
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Registrar evaluación'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}

// -------------------------------------------------------------------
//  Tab Aptitud — APTO/NO APTO por tipo de limpieza (FASE 3.2B-iv)
//
//  DECISIÓN DE PRODUCTO CERRADA: esto es un aviso, nunca un bloqueo. No hay
//  ningún botón deshabilitado ni flujo cortado por `apto=false` en ningún
//  sitio de la aplicación — esta pestaña sólo informa.
// -------------------------------------------------------------------

type FiltroAptitud = 'TODOS' | 'APTO' | 'NO_APTO';

function TabAptitud({ operario }: { operario: OperarioDetalle }): ReactNode {
  const aptitudes = useDatos(
    () => api.get<{ aptitudes: AptitudTipo[] }>(`/api/operarios/${operario.id}/aptitudes`),
    [operario.id],
  );
  const [seleccionada, setSeleccionada] = useState<AptitudTipo | null>(null);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<FiltroAptitud>('TODOS');

  if (seleccionada !== null) {
    return (
      <PanelDetalleAptitud
        operario={operario}
        aptitud={seleccionada}
        alVolver={() => setSeleccionada(null)}
      />
    );
  }

  return (
    <div className="panel-cuerpo sin-relleno">
      <Panel estado={aptitudes}>
        {(d) => {
          const t = busqueda.trim().toLowerCase();
          const filtradas = d.aptitudes.filter((a) => {
            if (filtro === 'APTO' && !a.apto) return false;
            if (filtro === 'NO_APTO' && a.apto) return false;
            if (t === '') return true;
            return (
              a.tipoCodigo.toLowerCase().includes(t) ||
              a.tipoDescripcion.toLowerCase().includes(t) ||
              a.clienteRazonSocial.toLowerCase().includes(t)
            );
          });

          return (
            <>
              <p className="tenue pequena" style={{ margin: '0 0 12px' }}>
                El estado de aptitud es informativo: no impide asignar a este operario a un tipo
                de limpieza, ni bloquea la generación, planificación o ejecución de OT.
              </p>

              <div className="filtros">
                <div className="campo campo-busqueda">
                  <label htmlFor="buscar-aptitud">Buscar</label>
                  <input
                    id="buscar-aptitud"
                    type="text"
                    placeholder="Cliente, código o descripción…"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                  />
                </div>
                <div className="campo">
                  <label htmlFor="filtro-aptitud">Estado</label>
                  <select
                    id="filtro-aptitud"
                    value={filtro}
                    onChange={(e) => setFiltro(e.target.value as FiltroAptitud)}
                  >
                    <option value="TODOS">Todos</option>
                    <option value="APTO">Aptos</option>
                    <option value="NO_APTO">No aptos</option>
                  </select>
                </div>
              </div>

              {d.aptitudes.length === 0 ? (
                <Vacio titulo="No hay tipos de limpieza disponibles para los clientes asociados a este operario" />
              ) : filtradas.length === 0 ? (
                <div className="panel-cuerpo">
                  <p className="suave">Sin resultados para los filtros actuales.</p>
                </div>
              ) : (
                <div className="tabla-desliza">
                  <table>
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th>Estado</th>
                        <th>Motivo</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {filtradas.map((a) => (
                        <tr key={a.idTipo}>
                          <td className="texto-secundario">{a.clienteRazonSocial}</td>
                          <td className="cod">{a.tipoCodigo}</td>
                          <td>{a.tipoDescripcion}</td>
                          <td>
                            <span className={`etiqueta ${a.apto ? 'exito' : 'aviso'}`}>
                              {a.apto ? 'APTO' : 'NO APTO'}
                            </span>
                          </td>
                          <td className="suave">{a.motivo ?? '—'}</td>
                          <td className="derecha">
                            <button className="pequeno" onClick={() => setSeleccionada(a)}>
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          );
        }}
      </Panel>
    </div>
  );
}

function PanelDetalleAptitud({
  operario,
  aptitud,
  alVolver,
}: {
  operario: OperarioDetalle;
  aptitud: AptitudTipo;
  alVolver: () => void;
}): ReactNode {
  const detalle = useDatos(
    () =>
      api.get<{ requisitos: RequisitoAptitud[] }>(
        `/api/operarios/${operario.id}/aptitudes/${aptitud.idTipo}`,
      ),
    [operario.id, aptitud.idTipo],
  );

  return (
    <div className="panel-cuerpo">
      <div style={{ marginBottom: 12 }}>
        <button type="button" className="enlace" onClick={alVolver}>
          ← Volver a Aptitud
        </button>
        <h3 style={{ marginTop: 6, marginBottom: 4 }}>
          {aptitud.clienteRazonSocial} · {aptitud.tipoCodigo} — {aptitud.tipoDescripcion}
        </h3>
        <span className={`etiqueta ${aptitud.apto ? 'exito' : 'aviso'}`}>
          {aptitud.apto ? 'APTO' : 'NO APTO'}
        </span>
      </div>

      <p className="tenue pequena" style={{ marginBottom: 12 }}>
        Este estado es informativo: no bloquea la asignación de este operario a este tipo de
        limpieza, ni la generación o ejecución de OT.
      </p>

      <Panel estado={detalle}>
        {(d) =>
          d.requisitos.length === 0 ? (
            <Vacio titulo="Este tipo de limpieza no exige ninguna polivalencia" />
          ) : (
            <div className="tabla-desliza">
              <table>
                <thead>
                  <tr>
                    <th>Polivalencia</th>
                    <th className="num">Nivel mínimo</th>
                    <th className="num">Nivel actual</th>
                    <th>Obligatorio</th>
                    <th>Cumple</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {d.requisitos.map((r) => (
                    <tr key={r.idPolivalencia}>
                      <td>
                        {r.polivalenciaDescripcion}{' '}
                        <span className="cod suave">{r.polivalenciaCodigo}</span>
                      </td>
                      <td className="num">{r.nivelMinimo} %</td>
                      <td className="num">{r.nivelValido === null ? '—' : `${r.nivelValido} %`}</td>
                      <td>
                        <span className={`etiqueta ${r.obligatorio ? 'aviso' : 'neutra'}`}>
                          {r.obligatorio ? 'Obligatorio' : 'No obligatorio'}
                        </span>
                      </td>
                      <td>
                        <span className={`etiqueta ${r.cumple ? 'exito' : 'neutra'}`}>
                          {r.cumple ? 'Sí' : 'No'}
                        </span>
                      </td>
                      <td className="suave">{r.motivo ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Panel>
    </div>
  );
}

// -------------------------------------------------------------------
//  Utilidades
// -------------------------------------------------------------------

/**
 * FASE 8.2 — dominio operativo del trabajador. No da permisos: sólo decide
 * en qué listas de candidatos aparece (Planner de Limpieza: LIMPIEZA/AMBAS;
 * técnicos de partes de trabajo: MANTENIMIENTO/AMBAS).
 */
const ETIQUETA_ESPECIALIDAD: Readonly<Record<EspecialidadOperativa, string>> = {
  LIMPIEZA: 'Limpieza',
  MANTENIMIENTO: 'Mantenimiento',
  AMBAS: 'Limpieza y mantenimiento',
};

function SelectorEspecialidad({
  id, valor, cambiar, disabled, error,
}: {
  id: string; valor: EspecialidadOperativa; cambiar: (v: EspecialidadOperativa) => void; disabled: boolean; error?: string | undefined;
}): ReactNode {
  return (
    <div className="campo">
      <label htmlFor={id}>Especialidad operativa</label>
      <select id={id} value={valor} disabled={disabled} onChange={(ev) => cambiar(ev.target.value as EspecialidadOperativa)}>
        {(Object.keys(ETIQUETA_ESPECIALIDAD) as EspecialidadOperativa[]).map((k) => (
          <option key={k} value={k}>{ETIQUETA_ESPECIALIDAD[k]}</option>
        ))}
      </select>
      {error !== undefined && <span className="campo-error">{error}</span>}
    </div>
  );
}

function BadgeTipo({ tipo }: { tipo: 'FIJO' | 'VOLANTE' | 'GLOBAL' }): ReactNode {
  return (
    <span className={`etiqueta ${tipo === 'FIJO' ? 'info' : tipo === 'GLOBAL' ? 'exito' : 'aviso'}`}>
      {tipo}
    </span>
  );
}
